import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { OutreachSender } from '@/types/outreach';
import { OUTREACH_SENDERS_FILTER, SENDER_DISPLAY_NAMES } from '@/types/outreach';

/**
 * Shared hook for loading outreach senders from team_profiles
 * and resolving the logged-in user's default sender.
 *
 * - Loads senders from team_profiles (has headline, about, position_description for AI)
 * - Resolves logged-in user's persona_id (sending_personas.id) → sending_personas.full_name → team_profiles match
 * - Returns resolvedSenderId (team_profiles.id) for auto-selection
 * - Returns isAdmin flag for sender-locked UX
 */
export function useOutreachSenders() {
  const { userProfile } = useAuth();
  const personaId = (userProfile as any)?.persona_id as string | null;
  const isAdmin = userProfile?.is_admin ?? false;

  // Load senders from team_profiles
  const { data: senders = [], isLoading } = useQuery({
    queryKey: ['outreach-senders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_profiles')
        .select('id, full_name, headline, about, position_description, heyreach_account_id, writing_guidelines, required_phrases, banned_phrases')
        .in('full_name', OUTREACH_SENDERS_FILTER)
        .eq('is_active', true);

      if (error) {
        console.error('Failed to load outreach senders:', error);
        return [];
      }

      return (data || []).map((tp: any): OutreachSender => {
        const hasRules = tp.writing_guidelines || tp.required_phrases?.length || tp.banned_phrases?.length;
        return {
          id: tp.id,
          name: SENDER_DISPLAY_NAMES[tp.full_name] || tp.full_name,
          headline: tp.headline,
          about: tp.about,
          positionDescription: tp.position_description,
          heyreachAccountId: tp.heyreach_account_id || null,
          writingRules: hasRules ? {
            writing_guidelines: tp.writing_guidelines || undefined,
            required_phrases: tp.required_phrases || undefined,
            banned_phrases: tp.banned_phrases || undefined,
          } : null,
        };
      });
    },
    staleTime: 5 * 60 * 1000,
  });

  // Resolve persona_id (sending_personas.id) → team_profiles.id via full_name
  const { data: resolvedSenderId = null } = useQuery({
    queryKey: ['outreach-sender-resolve', personaId],
    queryFn: async () => {
      if (!personaId) return null;

      // Get the sending_persona's full_name
      const { data: sp, error: spErr } = await supabase
        .from('sending_personas')
        .select('full_name')
        .eq('id', personaId)
        .maybeSingle();

      if (spErr || !sp) return null;

      const spName = (sp as any).full_name as string;

      // Find matching team_profiles sender
      const matched = senders.find(
        s => s.name === spName || s.name === (SENDER_DISPLAY_NAMES[spName] || spName)
      );
      return matched?.id || null;
    },
    enabled: !!personaId && senders.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  return { senders, isLoading, resolvedSenderId, isAdmin };
}
